import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, InlineNotification, Select, SelectItem, Tag, TextInput, Toggle } from '@carbon/react';
import { myShiftsFromApi, nextShift, openIn } from 'src/components/Schedule/scheduleModel';
import { useTenantContext } from 'src/context/useTenantContext';
import { useMoney } from 'src/hooks/useMoney';
import { useMySchedule } from 'src/hooks/useSchedules';
import {
  TimesheetView,
  useConfirmMyTimesheet,
  useMyCurrentShift,
  useMyPunch,
  useMyTimesheets,
  useSaveEmployeeNote,
} from 'src/hooks/useTimesheets';
import { formatMinutes, localTime, timesheetErrorKey } from './timesheetFormat';

dayjs.extend(isoWeek);

const DATE = 'YYYY-MM-DD';
const STATUS_TAG = { OPEN: 'blue', APPROVED: 'green', PAID: 'gray' } as const;

/** Resolves null on refusal, timeout (5 s) or no API — clock-in proceeds and the server flags it. */
export function getPosition(timeoutMs = 5000): Promise<{ lat: number; lng: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) =>
    navigator.geolocation.getCurrentPosition(
      (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
      () => resolve(null),
      { timeout: timeoutMs, maximumAge: 60_000 }
    )
  );
}

const needsConfirmation = (view: TimesheetView, today: string) =>
  view.timesheet.periodEnd < today && !view.timesheet.employeeConfirmedAt && view.timesheet.status !== 'PAID';

function EntryNote(props: { entryId: string; initial: string | null }) {
  const { t } = useTranslation();
  const save = useSaveEmployeeNote();
  const [note, setNote] = useState(props.initial ?? '');
  return (
    <div className="timesheet-card__note">
      <TextInput
        id={`timesheet-note-${props.entryId}`}
        size="sm"
        labelText={t('timesheet.disputeNote')}
        value={note}
        maxLength={500}
        onChange={(event) => setNote(event.target.value)}
      />
      <Button
        size="sm"
        kind="ghost"
        disabled={save.isLoading || note === (props.initial ?? '')}
        onClick={() => save.mutate({ entryId: props.entryId, note: note || null })}
      >
        {t('timesheet.saveNote')}
      </Button>
    </div>
  );
}

const TimesheetCard = () => {
  const { t } = useTranslation();
  const money = useMoney();
  const { buildPath } = useTenantContext();
  const { data: current } = useMyCurrentShift();
  const punch = useMyPunch();
  const confirm = useConfirmMyTimesheet();
  const [shiftName, setShiftName] = useState('');
  const [note, setNote] = useState('');
  const [paidBreak, setPaidBreak] = useState(false);
  const [noPosition, setNoPosition] = useState(false);

  const tz = current?.timezone ?? 'Asia/Ho_Chi_Minh';
  const now = dayjs().tz(tz);
  const today = now.format(DATE);
  const week = { from: now.startOf('isoWeek').format(DATE), to: now.endOf('isoWeek').format(DATE) };
  const month = { from: now.startOf('month').format(DATE), to: now.endOf('month').format(DATE) };
  // From the start of LAST month: a sheet that ended then is the one waiting to be confirmed,
  // and mine() only returns sheets whose period reaches `from`. The tiles filter by their own
  // ranges, so the extra days never leak into the week/month figures.
  const { data: range } = useMyTimesheets(
    now.subtract(1, 'month').startOf('month').format(DATE),
    week.to > month.to ? week.to : month.to
  );

  // Two weeks ahead: enough for "next shift" and this week's open seats (spec §7.3).
  const schedule = useMySchedule(today, now.add(13, 'day').format(DATE));
  const myShifts = schedule.isSuccess ? myShiftsFromApi(schedule.data) : [];
  const next = nextShift(myShifts);
  const openThisWeek = openIn(myShifts, week.from, week.to, tz);

  const days = range?.days ?? [];
  const sumPay = (from: string, to: string) =>
    days.filter((d) => d.date >= from && d.date <= to).reduce((s, d) => s + d.pay, 0);
  const sumMin = (from: string, to: string) =>
    days.filter((d) => d.date >= from && d.date <= to).reduce((s, d) => s + d.payableMin, 0);
  const timesheets = range?.timesheets ?? [];
  const currentPeriod = timesheets.find((v) => v.timesheet.periodStart <= today && v.timesheet.periodEnd >= today);

  const clockIn = async () => {
    const position = await getPosition();
    setNoPosition(position === null);
    punch.mutate({
      action: 'IN',
      shiftName: shiftName || undefined,
      note: note || undefined,
      lat: position?.lat,
      lng: position?.lng,
    });
  };

  const openBreak = current?.entry?.breaks.find((b) => b.endAt === null);
  // A shift left open since another day (a forgotten clock-out) must say which day.
  const since = (iso: string) =>
    dayjs(iso).tz(tz).format(DATE) === today ? localTime(iso, tz) : dayjs(iso).tz(tz).format('DD/MM HH:mm');

  return (
    <div className="timesheet-card" data-testid="timesheet-card">
      <h5 className="section__heading">{t('timesheet.title')}</h5>
      <Link to={buildPath('me/calendar')} data-testid="timesheet-open-calendar">
        {t('timesheet.calendar.openMine')}
      </Link>

      {/* Controller ruling: next shift / open shifts only render once the schedule fetch has
          actually succeeded — loading renders nothing, and a failure gets its own short line,
          rather than ever showing a false "No upcoming shift" / "Open shifts: 0". */}
      {schedule.isError && <p data-testid="timesheet-schedule-error">{t('schedule.errors.generic')}</p>}
      {schedule.isSuccess && (
        <div className="timesheet-card__schedule" data-testid="timesheet-next-shift">
          <span>{t('schedule.nextShift')}: </span>
          {next ? (
            <>
              <span>{dayjs(next.startAt).tz(tz).format('ddd DD/MM')}</span>
              <span aria-hidden> </span>
              <span>{localTime(next.startAt, tz)}</span>
              <span>–</span>
              <span>{localTime(next.endAt, tz)}</span>
              {next.shiftName && (
                <>
                  <span aria-hidden> · </span>
                  <span>{next.shiftName}</span>
                </>
              )}
            </>
          ) : (
            <span>{t('schedule.noUpcomingShift')}</span>
          )}
          <Link to={buildPath('me/calendar')} data-testid="timesheet-open-shifts">
            <span>{t('schedule.openShifts')}: </span>
            <span>{openThisWeek.length}</span>
          </Link>
        </div>
      )}

      {current?.state === 'OUT' && (
        <div className="timesheet-card__actions">
          {current.shiftNames.length > 0 && (
            <Select
              id="timesheet-shift"
              data-testid="timesheet-shift"
              labelText={t('timesheet.shift')}
              value={shiftName}
              onChange={(event) => setShiftName(event.target.value)}
            >
              <SelectItem value="" text="—" />
              {current.shiftNames.map((name) => (
                <SelectItem key={name} value={name} text={name} />
              ))}
            </Select>
          )}
          <TextInput
            id="timesheet-task"
            labelText={t('timesheet.taskNote')}
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button size="lg" data-testid="timesheet-clock-in" disabled={punch.isLoading} onClick={clockIn}>
            {t('timesheet.clockIn')}
          </Button>
        </div>
      )}

      {current?.entry && (
        <div className="timesheet-card__actions">
          {/* Time is appended as its own text node rather than an i18next interpolation
              value: it is a formatted wall-clock string, not translatable content, and
              keeping it out of the {{}} placeholder means a missing/mocked t() still
              renders the two together intelligibly. */}
          <p data-testid="timesheet-since">
            {openBreak
              ? `${t('timesheet.onBreakSince')} ${since(openBreak.startAt)}`
              : `${t('timesheet.onShiftSince')} ${since(current.entry.clockInAt)}`}
          </p>
          {current.state === 'IN' && (
            <>
              <Toggle
                id="timesheet-paid-break"
                size="sm"
                labelText={t('timesheet.paidBreak')}
                toggled={paidBreak}
                onToggle={(checked: boolean) => setPaidBreak(checked)}
              />
              <Button
                kind="secondary"
                data-testid="timesheet-break-start"
                disabled={punch.isLoading}
                onClick={() => punch.mutate({ action: 'BREAK_START', paid: paidBreak })}
              >
                {t('timesheet.startBreak')}
              </Button>
            </>
          )}
          {current.state === 'BREAK' && (
            <Button
              kind="secondary"
              data-testid="timesheet-break-end"
              disabled={punch.isLoading}
              onClick={() => punch.mutate({ action: 'BREAK_END' })}
            >
              {t('timesheet.endBreak')}
            </Button>
          )}
          <Button
            kind="danger"
            data-testid="timesheet-clock-out"
            disabled={punch.isLoading}
            onClick={() => punch.mutate({ action: 'OUT' })}
          >
            {t('timesheet.clockOut')}
          </Button>
        </div>
      )}

      {noPosition && (
        <InlineNotification
          data-testid="timesheet-location-unavailable"
          kind="warning"
          lowContrast
          hideCloseButton
          title={t('timesheet.locationUnavailable')}
        />
      )}
      {punch.error ? (
        <InlineNotification
          data-testid="timesheet-error"
          kind="error"
          lowContrast
          hideCloseButton
          title={t(timesheetErrorKey(punch.error))}
        />
      ) : null}
      {confirm.error ? (
        <InlineNotification
          data-testid="timesheet-confirm-error"
          kind="error"
          lowContrast
          hideCloseButton
          title={t(timesheetErrorKey(confirm.error))}
        />
      ) : null}

      <div className="stats timesheet-card__earnings">
        <div data-testid="timesheet-week">
          <span>{t('timesheet.thisWeek')}</span>
          <strong>{money.format(sumPay(week.from, week.to))}</strong>
          <span>{formatMinutes(sumMin(week.from, week.to))}</span>
        </div>
        <div data-testid="timesheet-month">
          <span>{t('timesheet.thisMonth')}</span>
          <strong>{money.format(sumPay(month.from, month.to))}</strong>
          <span>{formatMinutes(sumMin(month.from, month.to))}</span>
        </div>
      </div>

      {timesheets
        .filter((view) => needsConfirmation(view, today))
        .map((view) => (
          <div key={view.timesheet.id} className="timesheet-card__confirm">
            <span>
              {view.timesheet.periodStart} – {view.timesheet.periodEnd}
            </span>
            <Tag type={STATUS_TAG[view.timesheet.status]}>{t(`timesheet.status.${view.timesheet.status}`)}</Tag>
            <Button
              size="sm"
              data-testid={`timesheet-confirm-${view.timesheet.id}`}
              disabled={confirm.isLoading}
              onClick={() => confirm.mutate(view.timesheet.id)}
            >
              {t('timesheet.confirmHours')}
            </Button>
          </div>
        ))}

      {currentPeriod && (
        <div className="timesheet-card__days">
          <Tag type={STATUS_TAG[currentPeriod.timesheet.status]}>
            {t(`timesheet.status.${currentPeriod.timesheet.status}`)}
          </Tag>
          {currentPeriod.computation.days.length === 0 && <p>{t('timesheet.noEntries')}</p>}
          {currentPeriod.computation.days.map((day) => (
            <div key={day.date} className="timesheet-card__day">
              <strong>{dayjs(day.date).format('ddd DD/MM')}</strong>
              {day.entries.map((entry) => (
                <div key={entry.id} className="timesheet-card__entry">
                  <span>
                    {localTime(entry.clockInAt, tz)}–{entry.clockOutAt ? localTime(entry.clockOutAt, tz) : '…'}{' '}
                    {entry.shiftName}
                  </span>
                  <span>{formatMinutes(entry.payableMin)}</span>
                  {currentPeriod.timesheet.status === 'OPEN' && !entry.open && (
                    <EntryNote entryId={entry.id} initial={entry.employeeNote} />
                  )}
                </div>
              ))}
              <span>{money.format(day.pay)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimesheetCard;
